import { useEffect, useRef, useState } from 'react'

// Ad slot configuration
export const AD_SLOTS = {
  HOME_HERO: '1234567890',
  HOME_FOOTER: '1234567891',
  ROUTE_RESULTS: '1234567892',
  FARE_DISPLAY: '1234567893',
  TOURIST_FEED: '1234567894',
  LAYOUT_FOOTER: '1234567895',
  INTERSTITIAL: '1234567896',
}

type AdFormat = 'horizontal' | 'rectangle' | 'vertical' | 'fluid'

interface BannerAdProps {
  slot: string
  format?: AdFormat
  className?: string
  wrapperClassName?: string
}

declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}

function BannerAd({ slot, format = 'horizontal', className = '', wrapperClassName = '' }: BannerAdProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const insRef = useRef<HTMLModElement>(null)
  const isAdPushed = useRef(false)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Initialize adsbygoogle array if not present
    if (typeof window !== 'undefined') {
      window.adsbygoogle = window.adsbygoogle || []
    }

    // Push ad only once
    if (!isAdPushed.current && containerRef.current) {
      try {
        window.adsbygoogle.push({})
        isAdPushed.current = true
      } catch (err) {
        console.error('AdSense error:', err)
        setIsVisible(false)
      }
    }

    // Check if ad loaded after a delay
    const checkAdLoaded = setTimeout(() => {
      if (insRef.current) {
        // Check various indicators that an ad has loaded
        const adStatus = insRef.current.getAttribute('data-ad-status')
        const hasChildren = insRef.current.children.length > 0
        const hasHeight = insRef.current.offsetHeight > 10
        const isFilled = adStatus === 'filled'
        const isUnfilled = adStatus === 'unfilled'

        // Hide if explicitly unfilled or no content detected
        if (isUnfilled || (!isFilled && !hasChildren && !hasHeight)) {
          setIsVisible(false)
        }
      }
    }, 2500)

    return () => clearTimeout(checkAdLoaded)
  }, [])

  // Responsive ad styles based on format
  const getAdStyle = (): React.CSSProperties => {
    switch (format) {
      case 'horizontal':
        return { display: 'block', width: '100%', height: 'auto', minHeight: '50px' }
      case 'rectangle':
        return { display: 'inline-block', width: '300px', height: '250px' }
      case 'vertical':
        return { display: 'inline-block', width: '160px', height: '600px' }
      case 'fluid':
        return { display: 'block', width: '100%', height: 'auto' }
      default:
        return { display: 'block' }
    }
  }

  // Responsive format for horizontal ads
  const getResponsiveFormat = () => {
    if (format === 'horizontal' || format === 'fluid') {
      return 'auto'
    }
    return format === 'rectangle' ? 'rectangle' : 'vertical'
  }

  // Don't render anything if ad failed to load
  if (!isVisible) {
    return null
  }

  return (
    <div className={wrapperClassName}>
      <div ref={containerRef} className={`ad-container ${className}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={getAdStyle()}
          data-ad-client="ca-pub-3940256099942544"
          data-ad-slot={slot}
          data-ad-format={getResponsiveFormat()}
          data-full-width-responsive={format === 'horizontal' || format === 'fluid' ? 'true' : 'false'}
        />
      </div>
    </div>
  )
}

export default BannerAd
